// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerConstants.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import { toUpper, toLower } from 'lodash/fp';

const VALID_REPORT_NAME_REGEX = /^[^/<>&]*$/;

const DRILL_DOWN_STYLE_OPTION_VALUES = {
  link: 'link',
  linknewwindow: 'linknewwindow',
  embedded: 'embedded',
  popup: 'popup'
};

const JOIN_TYPES = {
  LEFT: 'Left',
  RIGHT: 'Right'
};

/* Izenda data type*/
const DATA_TYPES = {
  NUMERIC: 'Numeric',
  TEXT: 'Text',
  DATE_TIME: 'Datetime',
  TIME: 'Time',
  IMAGE: 'Image',
  BOOLEAN: 'Boolean',
  MONEY: 'Money',
  CUSTOM: 'CustomFormat',
  LOB: 'Lob',
  XML: 'XML',
  JSON: 'JSON'
};

const REPORT_TYPE_ENUM = {
  REPORT: 0,
  TEMPLATE: 1
};

const REPORT_TYPE = [
  {
    id: REPORT_TYPE_ENUM.REPORT,
    get name() {
      return localizer.getText('COMMON_LABEL_REPORTS');
    },
    value: 'Report'
  },
  {
    id: REPORT_TYPE_ENUM.TEMPLATE,
    get name() {
      return localizer.getText('COMMON_LABEL_TEMPLATES');
    },
    value: 'Template'
  }
];

const GLOBAL_REPORT_ENUM = {
  GLOBAL: 1,
  LOCAL: 0
};

const COMPARISION_TYPE = {
  NONE: 'none',
  FILTER: 'filter',
  FIELD: 'field',
  VALUE: 'value',
  DATE_TIME: 'datetime',
  DATE: 'date',
  TIME: 'time'
};

const dataSourceConcatTreeMap = (isExpand = false) => {
  return [
    {
      prop: 'querySource',
      nodeType: 'QuerySource',
      expand: isExpand,
      children: [
        {
          prop: 'fields',
          nodeType: 'Field'
        }
      ]
    }
  ];
};

const selectedDataSourceTreeMap = () => {
  return [
    {
      prop: 'querySource',
      nodeType: 'QuerySource',
      expand: false,
      children: []
    }
  ];
}

export default {
  COLUMN_NAME_PREFIX: {
    ORIGINAL_VALUE: 'org_',
    PERCENTAGE_VALUE: 'percentage_'
  },

  NO_SORT_DATA_TYPES: [DATA_TYPES.JSON, DATA_TYPES.LOB, DATA_TYPES.IMAGE, DATA_TYPES.XML],

  FILTER_URL: {
    OVERIDE_MASTER_REPORT_FILTER: {
      REGEX: /^mp(\d+)value$/,
      FORMAT: 'mp{0}value'
    },
    OVERIDE_REPORT_FILTER: {
      REGEX: /^p(\d+)value$/,
      FORMAT: 'p{0}value'
    },
    SUB_REPORT_FIELD_MAPPING_KEY: {
      FORMAT: 'fm{0}_{1}'
    },
    OVERIDE_MASTER_REPORT_USE_USER_LOOKUP: {
      REGEX: /^mp(\d+)lookup$/,
      FORMAT: 'mp{0}lookup'
    }
  },

  DATA_OBJECT_SWAP_KEYS: [
    ['joinDataSourceCategoryId', 'foreignDataSourceCategoryId'],
    ['JoinDataSourceCategoryName', 'foreignDataSourceCategoryName'],
    ['joinQuerySourceId', 'foreignQuerySourceId'],
    ['joinQuerySourceName', 'foreignQuerySourceName'],
    ['joinFieldId', 'foreignFieldId'],
    ['joinFieldName', 'foreignFieldName']
  ],

  /**
   * this is id of Uncategorized
   */
  get UNCATEGORIZED() {
    return localizer.getText('REPORTDESIGNER_LABEL_UNCATEGORIZED');
  },
  ADVANCED: 1,
  SIMPLE: 0,
  BUNDLE: {
    TOOLTIP: {
      FILTER: {
        get M07() {
          return localizer.getText('REPORTDESIGNER_FILTER_TOOLTIP');
        }
      }
    },
    CRITERIA_LIST_SIMPLE_MODE: [
      {
        value: 'All',
        get label() {
          return localizer.getText('COMMON_LABEL_ALL');
        }
      },
      {
        value: 'Category',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_SIMPLE_MODE_CATEGORY');
        }
      },
      {
        value: 'DatabaseName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_SIMPLE_MODE_DATABASE_NAME');
        }
      },
      {
        value: 'DataObject',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_SIMPLE_MODE_DATA_OBJECT');
        }
      },
      {
        value: 'DataObjectType',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_SIMPLE_MODE_DATA_OBJECT_TYPE');
        }
      },
      {
        value: 'SchemaName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_SIMPLE_MODE_SCHEMA_NAME');
        }
      }
    ],
    CRITERIA_LIST_ADVANCED_MODE: [
      {
        value: 'All',
        get label() {
          return localizer.getText('COMMON_LABEL_ALL');
        }
      },
      {
        value: 'Alias',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_ADVANCED_MODE_JOIN_ALIAS');
        }
      },
      {
        value: 'JoinDataSourceCategoryName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_ADVANCED_MODE_CATEGORY');
        }
      },
      {
        value: 'JoinQuerySourceName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_ADVANCED_MODE_DATA_OBJECT');
        }
      },
      {
        value: 'JoinFieldName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_ADVANCED_MODE_JOIN_FIELD');
        }
      },
      {
        value: 'ForeignQuerySourceName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_ADVANCED_MODE_FOREIGN_DATA_OBJECT');
        }
      },
      {
        value: 'ForeignFieldName',
        get label() {
          return localizer.getText('REPORTDESIGNER_LABEL_CRITERIA_LIST_ADVANCED_MODE_FIELD');
        }
      }
    ],
    SORT_ORDERS: {
      Category: 'DESC'
    },
    SORT_ORDERS_ADVANCED_MODE: {
      Alias: 'DESC',
      JoinType: '',
      Operator: '',
      JoinDataSourceCategoryId: '',
      JoinDataSourceCategoryName: '',
      JoinQuerySourceName: '',
      JoreignDataSourceCategoryId: '',
      JoreignDataSourceCategoryName: '',
      JoreignQuerySourceName: ''
    },
    MESSAGE: {
      get CANCEL() {
        return localizer.getText('COMMON_MESSAGES_CONFIRM_CHANGES_LOST');
      },
      get SAVE() {
        return localizer.getText('COMMON_MESSAGES_CONFIRM_SAVE');
      },
      get DELETE_RELATIONSHIP() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_CONFIRM_DELETE_RELATIONSHIP');
      },
      get DELETE_RELATIONSHIP_WITH_KEYJOIN() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_CONFIRM_DELETE_RELATIONSHIP_WITH_KEYJOIN');
      },
      get DELETE_KEYJOIN() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_DELETE_KEYJOIN');
      },
      get ALLOW_NULL_TOOLTIP() {
        return localizer.getText('REPORTDESIGNER_ALLOW_NULL_TOOLTIP');
      },
      get M01() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_COLUMN_NAME_REQUIRED');
      },
      get M02() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_FIELD_CANNOT_BE_FILTER');
      },
      get M03() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_CANNOT_DROP_RELATIONSHIP_IN_KEYJOIN_DEST');
      },
      get M04() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_CANNOT_DROP_KEYJOIN_HAVING_CROSS_JOIN');
      },
      get M06() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_VALID_SQL_STATEMENT');
      },
      get M08() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_FIELD_NAME_EXISTS');
      },
      get M09() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_FIELD_NAME_EXISTS_IN_REPORT_PART');
      },
      get M10() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_CONFIRM_SAVE_FILTER');
      },
      get M11() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_ADJUST_NUMBER_DATA_OBJECTS');
      },
      get M16() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_DATA_SOURCES_EXCEEDED_LIMITATION');
      },
      get SIMPLE_UNSELECT() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_SIMPLE_UNSELECT');
      },
      get ADVANCED_UNSELECT() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_ADVANCED_UNSELECT');
      },
      get SELECT_CROSS_JOIN() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_SELECT_CROSS_JOIN');
      },
      get CHANGE_JOIN_TYPE() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_CHANGE_JOIN_TYPE');
      },
      get FIELDS_EXCEEDED_LIMITATION() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_FIELDS_IN_REPORT_PART_EXCEEDED_LIMITATION');
      },
      get OPEN_SUB_REPORT_WARNING() {
        return localizer.getText('OPEN_SUB_REPORT_WARNING_MESSAGE');
      },
      get CANNOT_ADD_RUNNIG_FILTER() {
        return localizer.getText('REPORT_CANNOT_ADD_RUNNING_FILTER');
      },
      get CANNOT_ADD_REPEATER_RUNNING_FIELD() {
        return localizer.getText('FORM_CANNOT_ADD_REPEATER_RUNNING_FIELD');
      },
      get FILTER_WILL_BE_IGNORED() {
        return localizer.getText('UNSPECIFIED_FILTER_WILL_BE_IGNORED');
      }
    },

    DEFAULT_CRITERIA: 'All'
  },
  TABLE_CONFIG: {
    Table: {
      rowsCount: 10,
      rowHeight: 60,
      headerHeight: 60,
      width: 1400,
      height: 362,
      maxHeight: 800
    },
    Column: {
      width: 100
    }
  },
  ICON: {
    /* icon for stored-procedure */
    Table: 'icon-table',
    'Stored Procedure': 'icon-sp',
    Function: 'icon-fx-table',
    Relationship: 'icon-relationship',
    Schema: 'icon-schema',
    View: 'icon-view-table',

    /* icon for field type */
    Numeric: 'icon-type-no',
    Text: 'icon-type-text',
    Datetime: 'icon-type-calendar',
    Image: 'icon-picture',
    Boolean: 'icon-type-yn',
    Money: 'icon-money',
    Lob: 'icon-type-lob',
    Time: 'icon-clock'
  },
  TABLE_DEF: {
    SIMPLE_MODE: {
      category: {
        columnKey: 'Category',
        width: 250,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_SIMPLE_MODE_CATEGORY');
        },
        sortable: true
      },
      dbName: {
        columnKey: 'DatabaseName',
        width: 250,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_SIMPLE_MODE_DATABASE_NAME');
        },
        sortable: true
      },
      schemaName: {
        columnKey: 'SchemaName',
        width: 250,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_SIMPLE_MODE_SCHEMA_NAME');
        },
        sortable: true
      },
      dataObject: {
        columnKey: 'DataObject',
        width: 250,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_SIMPLE_MODE_DATA_OBJECT');
        },
        sortable: true
      },
      dataObjectType: {
        columnKey: 'DataObjectType',
        width: 250,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_SIMPLE_MODE_DATA_OBJECT_TYPE');
        },
        sortable: true
      }
    },
    ADVANCED_MODE: {
      parentId: { key: 'id' },
      alias: {
        key: 'alias',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_ALIAS');
        }
      },
      joinType: {
        key: 'joinType',
        width: 100,
        get title() {
          return localizer.getText('COMMON_LABEL_JOIN_TYPE');
        }
      },
      operator: {
        key: 'operator',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_OPERATOR');
        }
      },
      category: {
        key: 'joinDataSourceCategoryId',
        labelKey: 'joinDataSourceCategoryName',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_CATEGORY');
        }
      },
      dataObject: {
        key: 'joinQuerySourceId',
        labelKey: 'joinQuerySourceName',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_DATA_OBJECT');
        }
      },
      selectedJoinSourceAlias: {
        key: 'selectedJoinSourceAlias',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_SELECTED_JOIN_SOURCE_ALIAS');
        },
        sortable: true
      },
      joinField: {
        key: 'joinFieldId',
        labelKey: 'joinFieldName',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_JOIN_FIELD');
        }
      },
      comparisonOperator: { width: 100, align: 'center', key: 'comparisonOperator' },
      foreignCategory: {
        key: 'foreignDataSourceCategoryId',
        labelKey: 'foreignDataSourceCategoryName',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_FOREIGN_CATEGORY');
        }
      },
      foreignDataObject: {
        key: 'selectedForeignAlias',
        labelKey: 'foreignQuerySourceName',
        width: 110,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_FOREIGN_DATA_OBJECT');
        }
      },
      selectedForeignSourceAlias: {
        key: 'selectedForeignSourceAlias',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_FOREIGN_SOURCE_ALIAS');
        },
        sortable: true
      },
      foreignJoinfield: {
        key: 'foreignFieldId',
        labelKey: 'foreignFieldName',
        width: 100,
        get title() {
          return localizer.getText('REPORTDESIGNER_LABEL_TABLE_DEF_ADVANCED_MODE_FOREIGN_FIELD_NAME');
        }
      },
      action: {
        width: 80,
        get title() {
          return localizer.getText('COMMON_LABEL_ACTION');
        }
      }
    }
  },
  DEFAULT_COMPARISION_OPERATOR: '= (Field)',
  DEFAULT_COMPARISION_FILTER_OPERATOR: '= (Filter)',
  COMPARISION_OPTIONS: [
    {
      label: localizer.getText('COMPARISION_IS_NULL'),
      value: 'IS NULL',
      type: COMPARISION_TYPE.NONE,
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ]
    },
    {
      label: localizer.getText('COMPARISION_IS_NOT_NULL'),
      value: 'IS NOT NULL',
      type: COMPARISION_TYPE.NONE,
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ]
    },
    {
      label: localizer.getText('COMPARISION_IS_BLANK'),
      value: 'IS BLANK',
      type: COMPARISION_TYPE.NONE,
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ]
    },
    {
      label: localizer.getText('COMPARISION_IS_NOT_BLANK'),
      value: 'IS NOT BLANK',
      type: COMPARISION_TYPE.NONE,
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ]
    },
    // Filter Comparision
    {
      label: `= (${localizer.getText('COMPARISION_FILTER')})`,
      value: '= (Filter)',
      type: COMPARISION_TYPE.FILTER,
      group: localizer.getText('COMPARISION_FILTER_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY
      ]
    },
    // Field Comparision
    {
      label: `= (${localizer.getText('COMPARISION_FIELD')})`,
      value: '= (Field)',
      type: COMPARISION_TYPE.FIELD,
      group: localizer.getText('COMPARISION_FIELD_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ],
      forRelationship: true
    },
    {
      label: `<> (${localizer.getText('COMPARISION_FIELD')})`,
      value: '<> (Field)',
      type: COMPARISION_TYPE.FIELD,
      group: localizer.getText('COMPARISION_FIELD_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ],
      forRelationship: true
    },
    {
      label: `> (${localizer.getText('COMPARISION_FIELD')})`,
      value: '> (Field)',
      type: COMPARISION_TYPE.FIELD,
      group: localizer.getText('COMPARISION_FIELD_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ],
      forRelationship: true
    },
    {
      label: `< (${localizer.getText('COMPARISION_FIELD')})`,
      value: '< (Field)',
      type: COMPARISION_TYPE.FIELD,
      group: localizer.getText('COMPARISION_FIELD_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ],
      forRelationship: true
    },
    {
      label: `>= (${localizer.getText('COMPARISION_FIELD')})`,
      value: '>= (Field)',
      type: COMPARISION_TYPE.FIELD,
      group: localizer.getText('COMPARISION_FIELD_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ],
      forRelationship: true
    },
    {
      label: `<= (${localizer.getText('COMPARISION_FIELD')})`,
      value: '<= (Field)',
      type: COMPARISION_TYPE.FIELD,
      group: localizer.getText('COMPARISION_FIELD_COMPARISON'),
      DATA_TYPES: [
        DATA_TYPES.NUMERIC,
        DATA_TYPES.TEXT,
        DATA_TYPES.DATE_TIME,
        DATA_TYPES.TIME,
        DATA_TYPES.IMAGE,
        DATA_TYPES.BOOLEAN,
        DATA_TYPES.MONEY,
        DATA_TYPES.LOB,
        DATA_TYPES.XML,
        DATA_TYPES.JSON
      ],
      forRelationship: true
    },
    // Value Comparision
    {
      label: '=',
      value: '=',
      type: COMPARISION_TYPE.VALUE,
      group: localizer.getText('COMPARISION_VALUE_COMPARISON'),
      DATA_TYPES: [DATA_TYPES.NUMERIC, DATA_TYPES.TEXT, DATA_TYPES.MONEY]
    },
    {
      label: '<>',
      value: '<>',
      type: COMPARISION_TYPE.VALUE,
      group: localizer.getText('COMPARISION_VALUE_COMPARISON'),
      DATA_TYPES: [DATA_TYPES.NUMERIC, DATA_TYPES.TEXT, DATA_TYPES.MONEY]
    },
    {
      label: '>',
      value: '>',
      type: COMPARISION_TYPE.VALUE,
      group: localizer.getText('COMPARISION_VALUE_COMPARISON'),
      DATA_TYPES: [DATA_TYPES.NUMERIC, DATA_TYPES.TEXT, DATA_TYPES.MONEY]
    },
    {
      label: '<',
      value: '<',
      type: COMPARISION_TYPE.VALUE,
      group: localizer.getText('COMPARISION_VALUE_COMPARISON'),
      DATA_TYPES: [DATA_TYPES.NUMERIC, DATA_TYPES.TEXT, DATA_TYPES.MONEY]
    },
    {
      label: '>=',
      value: '>=',
      type: COMPARISION_TYPE.VALUE,
      group: localizer.getText('COMPARISION_VALUE_COMPARISON'),
      DATA_TYPES: [DATA_TYPES.NUMERIC, DATA_TYPES.TEXT, DATA_TYPES.MONEY]
    },
    {
      label: '<=',
      value: '<=',
      type: COMPARISION_TYPE.VALUE,
      group: localizer.getText('COMPARISION_VALUE_COMPARISON'),
      DATA_TYPES: [DATA_TYPES.NUMERIC, DATA_TYPES.TEXT, DATA_TYPES.MONEY]
    },
    // Date & Time
    {
      label: `= (${localizer.getText('COMPARISION_DATE_AND_TIME')})`,
      value: '= (Date & Time)',
      type: COMPARISION_TYPE.DATE_TIME,
      group: localizer.getText('COMPARISION_DATE_AND_TIME'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `<> (${localizer.getText('COMPARISION_DATE_AND_TIME')})`,
      value: '<> (Date & Time)',
      type: COMPARISION_TYPE.DATE_TIME,
      group: localizer.getText('COMPARISION_DATE_AND_TIME'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `> (${localizer.getText('COMPARISION_DATE_AND_TIME')})`,
      value: '> (Date & Time)',
      type: COMPARISION_TYPE.DATE_TIME,
      group: localizer.getText('COMPARISION_DATE_AND_TIME'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `< (${localizer.getText('COMPARISION_DATE_AND_TIME')})`,
      value: '< (Date & Time)',
      type: COMPARISION_TYPE.DATE_TIME,
      group: localizer.getText('COMPARISION_DATE_AND_TIME'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `>= (${localizer.getText('COMPARISION_DATE_AND_TIME')})`,
      value: '>= (Date & Time)',
      type: COMPARISION_TYPE.DATE_TIME,
      group: localizer.getText('COMPARISION_DATE_AND_TIME'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `<= (${localizer.getText('COMPARISION_DATE_AND_TIME')})`,
      value: '<= (Date & Time)',
      type: COMPARISION_TYPE.DATE_TIME,
      group: localizer.getText('COMPARISION_DATE_AND_TIME'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    // Date
    {
      label: `= (${localizer.getText('COMPARISION_DATE')})`,
      value: '= (Date)',
      type: COMPARISION_TYPE.DATE,
      group: localizer.getText('COMPARISION_DATE'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `<> (${localizer.getText('COMPARISION_DATE')})`,
      value: '<> (Date)',
      type: COMPARISION_TYPE.DATE,
      group: localizer.getText('COMPARISION_DATE'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `> (${localizer.getText('COMPARISION_DATE')})`,
      value: '> (Date)',
      type: COMPARISION_TYPE.DATE,
      group: localizer.getText('COMPARISION_DATE'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `< (${localizer.getText('COMPARISION_DATE')})`,
      value: '< (Date)',
      type: COMPARISION_TYPE.DATE,
      group: localizer.getText('COMPARISION_DATE'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `>= (${localizer.getText('COMPARISION_DATE')})`,
      value: '>= (Date)',
      type: COMPARISION_TYPE.DATE,
      group: localizer.getText('COMPARISION_DATE'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    {
      label: `<= (${localizer.getText('COMPARISION_DATE')})`,
      value: '<= (Date)',
      type: COMPARISION_TYPE.DATE,
      group: localizer.getText('COMPARISION_DATE'),
      DATA_TYPES: [DATA_TYPES.DATE_TIME]
    },
    // Time
    {
      label: `= (${localizer.getText('COMPARISION_TIME')})`,
      value: '= (Time)',
      type: COMPARISION_TYPE.TIME,
      group: localizer.getText('COMPARISION_TIME'),
      DATA_TYPES: [DATA_TYPES.TIME]
    },
    {
      label: `<> (${localizer.getText('COMPARISION_TIME')})`,
      value: '<> (Time)',
      type: COMPARISION_TYPE.TIME,
      group: localizer.getText('COMPARISION_TIME'),
      DATA_TYPES: [DATA_TYPES.TIME]
    },
    {
      label: `> (${localizer.getText('COMPARISION_TIME')})`,
      value: '> (Time)',
      type: COMPARISION_TYPE.TIME,
      group: localizer.getText('COMPARISION_TIME'),
      DATA_TYPES: [DATA_TYPES.TIME]
    },
    {
      label: `< (${localizer.getText('COMPARISION_TIME')})`,
      value: '< (Time)',
      type: COMPARISION_TYPE.TIME,
      group: localizer.getText('COMPARISION_TIME'),
      DATA_TYPES: [DATA_TYPES.TIME]
    },
    {
      label: `>= (${localizer.getText('COMPARISION_TIME')})`,
      value: '>= (Time)',
      type: COMPARISION_TYPE.TIME,
      group: localizer.getText('COMPARISION_TIME'),
      DATA_TYPES: [DATA_TYPES.TIME]
    },
    {
      label: `<= (${localizer.getText('COMPARISION_TIME')})`,
      value: '<= (Time)',
      type: COMPARISION_TYPE.TIME,
      group: localizer.getText('COMPARISION_TIME'),
      DATA_TYPES: [DATA_TYPES.TIME]
    },
    // Boolean
    {
      label: toUpper(localizer.getText('FILTER_DETAIL_CONFIG_CONSTANTS_VALUE_LIST_TRUE')),
      value: 'TRUE',
      type: COMPARISION_TYPE.NONE,
      group: localizer.getText('COMPARISION_BOOLEAN'),
      DATA_TYPES: [DATA_TYPES.BOOLEAN]
    },
    {
      label: toUpper(localizer.getText('FILTER_DETAIL_CONFIG_CONSTANTS_VALUE_LIST_FALSE')),
      value: 'FALSE',
      type: COMPARISION_TYPE.NONE,
      group: localizer.getText('COMPARISION_BOOLEAN'),
      DATA_TYPES: [DATA_TYPES.BOOLEAN]
    }
  ],
  COMPARISION_TYPE,
  GENNERAL: {
    MIN_WIDTH: 155,
    FIELD_ITEM: 'FieldItem',
    FILTER_ITEM: 'FilterItem',
    BORDER_HOVER_ITEM: {
      COLOR: '#73AA2C',
      STYLE: 'solid'
    }
  },
  FIELDS: {
    get FILTERS_HEADER_LABEL() {
      return localizer.getText('REPORTDESIGNER_LABEL_FIELDS_FILTERS_HEADER');
    },
    get SHOW_FILTERS_LABEL() {
      return localizer.getText('REPORTDESIGNER_LABEL_FIELDS_SHOW_FILTERS');
    },
    get ADD_FILTER_LABEL() {
      return localizer.getText('REPORTDESIGNER_LABEL_FIELDS_ADD_FILTER');
    },
    get FREEZE_LABEL() {
      return localizer.getText('REPORTDESIGNER_LABEL_FIELDS_FREEZE');
    },
    get FILTER_LOGIC_LABEL() {
      return localizer.getText('REPORTDESIGNER_LABEL_FIELDS_FILTER_LOGIC');
    },
    get FILTER_LOGIC_PLACEHOLDER() {
      return localizer.getText('REPORTDESIGNER_PLACEHOLDER_FIELDS_FILTER_LOGIC');
    },
    get VALIDATE_SYNTAX_LABEL() {
      return localizer.getText('REPORTDESIGNER_LABEL_FIELDS_VALIDATE_SYNTAX');
    },
    get VALID_FILTER_LOGIC_MESSAGE() {
      return localizer.getText('REPORTDESIGNER_MESSAGE_VALID_FILTERLOGIC_STATEMENT');
    },
    get M24() {
      return localizer.getText('REPORTDESIGNER_FILTER_FIELDS_TYPE_VALIDATE');
    },
    get M25() {
      return localizer.getText('REPORTDESIGNER_FILTER_FIELDS_TYPE_VALIDATE_INFO');
    },
    NUMBER_OF_COLUMN: 4,
    FITLERS_PANEL_UI: {
      minHeight: 55,
      maxHeight: 210
    }
  },
  DATA_SOURCE: {
    FITLERS_PANEL_UI: {
      minHeight: 55,
      maxHeight: 210
    }
  },
  MENU_DEFINITION: [
    {
      key: 'DataSource',
      icon: 'icon-data-source',
      get title() {
        return localizer.getText('COMMON_LABEL_DATA_SOURCE');
      },
      get tooltip() {
        return localizer.getText('COMMON_TOOLTIP_DATA_SOURCE');
      },
      hoverShowTooltip: true
    },
    {
      key: 'Fields',
      icon: 'icon-design-icon',
      get title() {
        return localizer.getText('REPORTDESIGNER_LABEL_MENU_DEF_DESIGN');
      },
      get tooltip() {
        return localizer.getText('REPORTDESIGNER_TOOLTIP_MENU_DEF_DESIGN');
      },
      hoverShowTooltip: true
    },
    {
      key: 'Formats',
      icon: 'icon-format',
      get title() {
        return localizer.getText('REPORTDESIGNER_LABEL_MENU_DEF_FORMAT');
      },
      get tooltip() {
        return localizer.getText('REPORTDESIGNER_TOOLTIP_MENU_DEF_FORMAT');
      },
      hoverShowTooltip: true
    },
    {
      key: 'Exporting',
      icon: 'icon-export',
      get title() {
        return localizer.getText('COMMON_LABEL_EXPORTING');
      },
      licenseKey: LicenseCheckerConstants.LICENSE_KEY.Exporting,
      excludeForMobile: true,
      get tooltip() {
        return localizer.getText('COMMON_TOOLTIP_EXPORTING');
      },
      hoverShowTooltip: true
    },
    {
      key: 'Schedule',
      icon: 'icon-schedule',
      get title() {
        return localizer.getText('REPORTDESIGNER_LABEL_MENU_DEF_SCHEDULE');
      },
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsActionsSchedule],
      licenseKey: LicenseCheckerConstants.LICENSE_KEY.Scheduling,
      get tooltip() {
        return localizer.getText('REPORTDESIGNER_TOOLTIP_MENU_DEF_SCHEDULE');
      },
      hoverShowTooltip: true
    },
    {
      key: 'Access',
      icon: 'icon-access',
      get title() {
        return localizer.getText('REPORTDESIGNER_LABEL_MENU_DEF_ACCESS');
      },
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsActionsConfigureAccessRights],
      get tooltip() {
        return localizer.getText('REPORTDESIGNER_TOOLTIP_MENU_DEF_ACCESS');
      },
      hoverShowTooltip: true
    }
  ],

  REPORT_NAME: {
    presence: {
      presence: true,
      get message() {
        return localizer.getText('REPORTDESIGNER_MESSAGE_REPORT_NAME_REQUIRED');
      }
    }
  },
  CONSTRAINTS: {
    ADVANCED: {
      alias: {
        storeAction: {
          action: 'validateAlias'
        }
      },
      joinDataSourceCategoryId: {
        presence: {
          presence: true,
          get message() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          }
        }
      },
      foreignDataSourceCategoryId: {
        presence: {
          presence: true,
          get message() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          }
        }
      },
      joinQuerySourceId: {
        presence: {
          presence: true,
          get message() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          }
        }
      },
      foreignQuerySourceId: {
        presence: {
          presence: true,
          get message() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          }
        }
      },
      joinFieldId: {
        presence: {
          presence: true,
          get message() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          }
        }
      },
      foreignFieldId: {
        presence: {
          presence: true,
          get message() {
            return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
          }
        }
      }
    },
    REPORT_NAME: {
      presence: {
        presence: true,
        get message() {
          return localizer.getText('REPORTDESIGNER_MESSAGE_REPORT_NAME_REQUIRED');
        }
      },
      format: {
        pattern: VALID_REPORT_NAME_REGEX,
        get message() {
          return localizer.getText('INVALID_REPORT_NAME');
        }
      }
    },
    TEMPLATE_NAME: {
      presence: {
        presence: true,
        get message() {
          return localizer.getText('REPORTDESIGNER_MESSAGE_TEMPLATE_NAME_REQUIRED');
        }
      },
      format: {
        pattern: VALID_REPORT_NAME_REGEX,
        get message() {
          return localizer.getText('INVALID_TEMPLATE_NAME');
        }
      }
    }
  },
  VALIDATION_KEY: 'validationKey',
  JOIN_TYPES: [
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_JOIN_TYPE_INNER');
      },
      value: 'Inner'
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_JOIN_TYPE_LEFT');
      },
      value: 'Left'
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_JOIN_TYPE_RIGHT');
      },
      value: 'Right'
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_JOIN_TYPE_CROSS');
      },
      value: 'Cross'
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_JOIN_TYPE_FULL');
      },
      value: 'Full'
    }
  ],
  SEARCH_ALL: 'all',
  JOIN_TYPE_CROSS: 'Cross',
  OPERATOR_TYPES: [
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_OPERATOR_TYPE_AND');
      },
      value: 'AND'
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_OPERATOR_TYPE_OR');
      },
      value: 'OR'
    }
  ],
  TYPES: REPORT_TYPE,
  GLOBAL_REPORT_ENUM,
  EMPTY_GUID: '00000000-0000-0000-0000-000000000000',
  FILTERS_LOGIC: {
    storeAction: {
      action: 'validateFilterSyntax'
    }
  },
  get DELETE_FILTER_MESSAGE() {
    return localizer.getText('REPORTDESIGNER_LABEL_DELETE_FILTER');
  },
  get CASCADING_CHANGED_MESSAGE() {
    return localizer.getText('REPORTDESIGNER_MESSAGE_CASCADING_CHANGED');
  },
  PREVIEW_RECORD: [
    { label: '10', value: 10 },
    { label: '100', value: 100 },
    { label: '1000', value: 1000 },
    { label: '10000', value: 10000 },
    {
      get label() {
        return localizer.getText('COMMON_LABEL_ALL');
      },
      value: -1
    }
  ],
  get DEFAULT_REPORT_NAME() {
    return localizer.getText('REPORTDESIGNER_LABEL_DEFAULT_REPORT_NAME');
  },
  get DEFAULT_TEMPLATE_NAME() {
    return localizer.getText('REPORTDESIGNER_LABEL_DEFAULT_TEMPLATE_NAME');
  },
  DEFAULT_PREVIEW_RECORD: 10,
  DEFAULT_PAGE_INDEX: 1,
  DEFAULT_PAGE_SIZE: 10,
  OBJECT_STATE: {
    NONE: 0,
    INSERT: 1,
    DELETE: 2,
    UPDATE: 3
  },
  getReportSection(section: string) {
    switch (toLower(section)) {
      case 'all':
        return 0;
      case 'datasource':
        return 1;
      case 'fields':
        return 2;
      default:
        return 0;
    }
  },
  get CANNOT_DELETE_PARAM_SP() {
    return localizer.getText('REPORTDESIGNER_LABEL_CANNOT_DELETE_PARAM_SP');
  },
  REQUIRED_CONSTRAINT: {
    presence: {
      presence: true,
      get message() {
        return localizer.getText('COMMON_CONSTRAINT_REQUIRED');
      }
    }
  },
  DRILL_DOWN_STYLE_OPTION_VALUES,
  DRIL_DOWN_STYLE_OPTIONS: [
    {
      get label() {
        return localizer.getText('COMMON_LABEL_LINK');
      },
      value: DRILL_DOWN_STYLE_OPTION_VALUES.link
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_DRILL_DOWN_STYLE_LINK_NEW_WINDOW');
      },
      value: DRILL_DOWN_STYLE_OPTION_VALUES.linknewwindow
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_DRILL_DOWN_STYLE_EMBEDDED');
      },
      value: DRILL_DOWN_STYLE_OPTION_VALUES.embedded
    },
    {
      get label() {
        return localizer.getText('REPORTDESIGNER_LABEL_DRILL_DOWN_STYLE_POPUP');
      },
      value: DRILL_DOWN_STYLE_OPTION_VALUES.popup
    }
  ],
  DRIL_DOWN_STYLE: {
    LINK: 'link',
    LINK_NEW_WINDOW: 'linknewwindow',
    EMBEDDED: 'embedded',
    POPUP: 'popup'
  },
  PAGE_NAME: {
    DATA_SOURCE: 'DataSource',
    FIELDS: 'Fields',
    FORMATS: 'Formats',
    EXPORTING: 'Exporting',
    SCHEDULE: 'Schedule',
    ACCESS: 'Access'
  },
  DATETIME_FORMAT: 'MM/DD/YYYY HH:mm',
  SIMPLE_DATETIME_FORMAT: 'MM/DD/YYYY',
  EXPRESSION_SELECTION: {
    MIDDLE_FIELD: {
      FIELD_NAME: {
        enableDataSource: true,
        returnedFieldNameFormat: '[{0}].[{1}].[{2}].[{3}]',
        returnedOnlyFieldNameFormat: '[{0}]'
      }
    },
    REPORT_PART: {
      FIELD_NAME: {
        enableDataSource: true,
        returnedFieldNameFormat: '[{0}].[{1}].[{2}].[{3}]',
        returnedOnlyFieldNameFormat: '[{0}]',
        subOrGrandTotal: true
      }
    }
  },
  DETECT_CHANGE: {
    PHYSICAL: 'physical',
    LOGICAL: 'logical'
  },
  REPORT_PART_TYPE: {
    Chart: '0',
    Form: '1',
    Gauge: '2',
    Grid: '3',
    Map: '4',
    KPI: '5'
  },
  JOIN_TYPE: JOIN_TYPES,
  DATA_TYPES,
  REPORT_TYPE_ENUM,

  EXPANDED_LEVEL: {
    ALL: -1,
    FIRST_LEVEL: 0
  },
  DEFAULT_VALUE_PROPERTIES: {
    HEADERS: {
      BACKGROUND_COLOR: '#E4E4E4'
    }
  },
  HINT_MESSAGE: {
    get CATEGORY() {
      return localizer.getText('REPORTDESIGNER_MESSAGE_HINT_CATEGORY');
    },
    get SUB_CATEGORY() {
      return localizer.getText('REPORTDESIGNER_MESSAGE_HINT_SUB_CATEGORY');
    }
  },
  REPORT_PART_PERMISSIONS: [
    {
      value: 0,
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsReportPartTypesChart]
    },
    {
      value: 1,
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsReportPartTypesForm]
    },
    {
      value: 2,
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsReportPartTypesGauge]
    },
    {
      value: 4,
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsReportPartTypesMap]
    },
    {
      value: 5,
      permissions: [AuthorizationConstants.PERMISSION_KEY.ReportsReportPartTypesKPI]
    }
  ],

  FIELD_TYPE: {
    AggregatedField: 'Aggregated Field'
  },

  EDITOR_CONFIG: {
    ReportPart: [
      {
        name: 'reportPartProps'
      }
    ]
  },
  CALCULATED_FIELD_NAME: 'Calculated Fields',
  REPORT_DESIGNER_TAB: ['DataSource', 'Fields', 'Formats', 'Exporting', 'Schedule', 'Access'],
  ERROR_KEY: {
    reportPart: 'reportPart',
    calculatedfield: 'calculatedfield',
    filter: 'filter',
    relationship: 'relationship'
  },
  MENU_ACTIVE_LINK: 'Fields',
  MENU_DATASOURCE_LINK: 'DataSource',
  REPORT_STATE: {
    POSTED: 0,
    SAVED: 1,
    DRAFT: 2
  },
  DATA_SOURCE_CONCAT_TREE_MAP: dataSourceConcatTreeMap(),
  SELECTED_DATA_SOURCE_TREE_MAP: selectedDataSourceTreeMap(),
  DATA_SOURCE_TREE_MAP: [
    {
      prop: 'category',
      expand: true,
      nodeType: 'Category',
      children: dataSourceConcatTreeMap()
    }
  ],
  DATA_SOURCE_TREE_MAP_FOR_SEARCH: [
    {
      prop: 'category',
      expand: true,
      nodeType: 'Category',
      children: dataSourceConcatTreeMap(true)
    }
  ],
  LAZY_LOADING_DATA_SOURCE_TREE_ITEMS: 50,
  VALID_REPORT_NAME_REGEX,
  MESSAGE_KEY: {
    DISTINCT: 'Distinct',
    CONCURRENCY: 'Concurrency'
  },
  FILTER_CHANGE_STATE: {
    ADDED: '1',
    UPDATED: '3'
  }
};



// WEBPACK FOOTER //
// ./ui/logic/data/Izenda.ReportDesignerConstants.js